import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import client from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { FeedPathTag } from "@/components/FeedPathTag";
import { useDealerHostSuffix } from "@/hooks/useDealerHostSuffix";
import { Globe, Clock, Pencil, Save, ExternalLink } from "lucide-react";

// Mirrors backend CMS_SITE_STATUS_ACTIONS (models.py) — which actions are
// available from each current cms_site_status.
const ACTIONS_BY_STATUS = {
  no_site: ["deploy"],
  offline: ["bring_online", "disable_site", "delete_site"],
  online: ["take_offline", "disable_site"],
  disabled: ["bring_online", "take_offline", "delete_site"],
  site_deleted: ["deploy"],
};

// Mirrors backend SITE_STATUS_ACTION_PERMISSION (models.py). A dealer's own
// Power Content Manager may take the site offline and bring it back;
// deploying, disabling and deleting a site are SAD's.
const DEPLOY_ACTIONS = ["deploy", "disable_site", "delete_site"];

function readinessMessage(result, t) {
  const params = result.message_params || {};
  if (result.message_key === "serviceHoursIncomplete") {
    return t(
      "dealership.dataReadinessMessages.serviceHoursIncomplete",
      t(`serviceTypes.${params.service_type}`),
    );
  }
  return t(`dealership.dataReadinessMessages.${result.message_key}`);
}

const BADGE_VARIANT_BY_STATUS = {
  no_site: "outline",
  offline: "secondary",
  online: "default",
  disabled: "secondary",
  site_deleted: "destructive",
};

// Mirrors backend HOST_RENAME_TOTAL_STEPS (models.py) — CMS rename, local
// rename, OneTrust domain update.
const HOST_RENAME_TOTAL_STEPS = 3;

// Mirrors backend SITE_ASSIGN_TOTAL_STEPS (models.py) — CMS assignment,
// local mapping.
const SITE_ASSIGN_TOTAL_STEPS = 2;

/** Client-side mirror of the backend's compute_dealer_readiness (models.py)
 * — non-persisted flags derived from data already on the dealer record. */
const computeDealerReadiness = (dealer) => {
  const cookieConsentsLocal = dealer.cookie_consents_local || {};
  return {
    consentsSetupCompleted: Boolean(
      dealer.cookie_consents_local_status === "Success" && cookieConsentsLocal.script_id,
    ),
    dnsSetupCompleted: true, // always true for now — more logic to come
  };
};

/** Client-side mirror of the backend's _extract_url_host — used as a
 * fallback for branches imported before url_host existed. */
const deriveUrlHost = (websiteUrl, hostSuffix) => {
  if (!websiteUrl) return "";
  let host = "";
  try {
    host = new URL(websiteUrl).hostname.toLowerCase();
  } catch {
    return "";
  }
  const suffix = `.${hostSuffix}`;
  return host.endsWith(suffix) ? host.slice(0, -suffix.length) : host;
};

export function SiteStatusCard({ dealer, dealerId, onSaved, canToggleStatus = false, canDeploy = false, canEditHost = true, canAssignExistingSite = false }) {
  const { t } = useI18n();
  const hostSuffix = useDealerHostSuffix();
  const [acting, setActing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmAction, setConfirmAction] = useState(null); // action awaiting a "are you sure?" confirmation
  const [editingWebsite, setEditingWebsite] = useState(false);
  const [urlHostDraft, setUrlHostDraft] = useState("");
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [confirmRenameOpen, setConfirmRenameOpen] = useState(false);
  const [checkingReadiness, setCheckingReadiness] = useState(false);
  const [readinessResult, setReadinessResult] = useState(null);
  const [websiteSaveError, setWebsiteSaveError] = useState("");
  const [assignInputOpen, setAssignInputOpen] = useState(false);
  const [siteGlobalIdDraft, setSiteGlobalIdDraft] = useState("");
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);
  const [assigningSite, setAssigningSite] = useState(false);
  const [siteAssignError, setSiteAssignError] = useState("");

  const status = dealer.cms_site_status || "no_site";
  const pending = dealer.cms_status_change_pending === true;
  const websiteUrl = dealer.website_url || "";
  const urlHostVal = dealer.url_host || deriveUrlHost(websiteUrl, hostSuffix);
  const helperUrl = dealer.helper_url || "";
  const readiness = computeDealerReadiness(dealer);
  const hostRename = dealer.host_rename || {};
  const hostRenamePending = hostRename.status === "in_progress";
  const hostRenameFailed = hostRename.status === "failed";
  const siteAssign = dealer.site_assign || {};
  const siteAssignPending = siteAssign.status === "in_progress";
  const siteAssignFailed = siteAssign.status === "failed";

  // While a rename or an "assign existing website" is in progress, the CMS
  // confirms completion via an inbound webhook of ours (see backend's
  // ext_host_rename_webhook / ext_site_assign_webhook) — poll the dealer's
  // own summary so this card picks up each checkpoint without the user
  // having to reload the page.
  useEffect(() => {
    if (!hostRenamePending && !siteAssignPending) return undefined;
    const interval = setInterval(async () => {
      try {
        const { data } = await client.get(`/dealers/${dealerId}/summary`);
        if (data?.dealer) onSaved(data.dealer);
      } catch {
        // Transient poll failures are fine — the next tick tries again.
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [hostRenamePending, siteAssignPending, dealerId]);

  const startEditWebsite = () => {
    setUrlHostDraft(urlHostVal);
    setEditingWebsite(true);
  };

  const saveWebsite = () => {
    setConfirmRenameOpen(true);
  };

  const confirmRenameWebsite = async () => {
    setConfirmRenameOpen(false);
    setSavingWebsite(true);
    try {
      const newHost = (urlHostDraft || "").trim().toLowerCase();
      const { data } = await client.post(`/dealers/${dealerId}/host-rename`, { new_host: newHost });
      onSaved(data);
      setEditingWebsite(false);
      toast.success(t("dealership.saved"));
    } catch (e) {
      setWebsiteSaveError(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingWebsite(false);
    }
  };

  const openAssignExistingSite = () => {
    setSiteGlobalIdDraft("");
    setAssignInputOpen(true);
  };

  const proceedToAssignConfirm = () => {
    if (!siteGlobalIdDraft.trim()) return;
    setAssignInputOpen(false);
    setAssignConfirmOpen(true);
  };

  const confirmAssignExistingSite = async () => {
    setAssignConfirmOpen(false);
    setAssigningSite(true);
    try {
      const siteGlobalId = siteGlobalIdDraft.trim();
      const { data } = await client.post(`/dealers/${dealerId}/site-assign`, { site_global_id: siteGlobalId });
      onSaved(data);
      toast.success(t("dealership.saved"));
    } catch (e) {
      setSiteAssignError(e?.response?.data?.detail || "Save failed");
    } finally {
      setAssigningSite(false);
    }
  };

  const runAction = async (action, extra = {}) => {
    setActing(true);
    try {
      const { data } = await client.post(`/dealers/${dealerId}/site-status/action`, { action, ...extra });
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update site status");
    } finally {
      setActing(false);
    }
  };

  const openDelete = () => {
    setConfirmText("");
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    await runAction("delete_site", { confirm_text: confirmText });
    setDeleteOpen(false);
  };

  const confirmSimpleAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    await runAction(action);
  };

  const checkReadiness = async () => {
    setCheckingReadiness(true);
    try {
      const { data } = await client.post(`/dealers/${dealerId}/data-readiness/check`);
      setReadinessResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("dealership.checkReadinessFailed"));
    } finally {
      setCheckingReadiness(false);
    }
  };

  return (
    <Card className="mb-6 p-6" data-testid="site-status-card">
      <div className="mb-4 flex items-center gap-2">
        <Globe className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-lg font-semibold">{t("dealership.dealerWebsiteTitle")}</h2>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        {pending ? (
          <Badge
            variant="outline"
            className="gap-2 px-3 py-1.5 text-base font-semibold text-muted-foreground"
            data-testid="site-status-pending-badge"
          >
            <Clock className="h-4 w-4" />
            {t("dealership.statusChangePending", t(`dealership.siteStatus.${dealer.cms_site_status_pending}`))}
          </Badge>
        ) : (
          <Badge
            variant={BADGE_VARIANT_BY_STATUS[status]}
            className="px-3 py-1.5 text-base font-semibold"
            data-testid="site-status-badge"
          >
            {t(`dealership.siteStatus.${status}`)}
          </Badge>
        )}

        {!pending && (canToggleStatus || canDeploy) && (
          <div className="flex flex-wrap gap-2">
            {(ACTIONS_BY_STATUS[status] || [])
              .filter((action) => (DEPLOY_ACTIONS.includes(action) ? canDeploy : canToggleStatus))
              .map((action) =>
              action === "delete_site" ? (
                <Button
                  key={action}
                  data-testid="site-status-action-delete_site"
                  variant="destructive"
                  size="sm"
                  disabled={acting}
                  onClick={openDelete}
                >
                  {t("dealership.siteStatusActions.delete_site")}
                </Button>
              ) : (
                <Button
                  key={action}
                  data-testid={`site-status-action-${action}`}
                  variant="outline"
                  size="sm"
                  disabled={acting}
                  onClick={() => setConfirmAction(action)}
                >
                  {t(`dealership.siteStatusActions.${action}`)}
                </Button>
              ),
            )}
          </div>
        )}
      </div>

      <div className="mt-6 border-t border-border pt-6">
        <div className="mb-1 flex items-center justify-between gap-1">
          <Label className="data-label flex items-center gap-1">
            {t("dealership.website")}
            <FeedPathTag path="Dealer.CustomWebsiteURL" />
          </Label>
          {canEditHost && !editingWebsite && !hostRenamePending && (
            <Button
              data-testid="edit-website-btn"
              variant="ghost"
              size="sm"
              className="h-6 gap-1 px-1.5 text-xs"
              onClick={startEditWebsite}
            >
              <Pencil className="h-3 w-3" />
              {t("common.edit")}
            </Button>
          )}
          {editingWebsite && (
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => setEditingWebsite(false)}>
                {t("common.cancel")}
              </Button>
              <Button
                data-testid="save-website-btn"
                size="sm"
                className="h-6 gap-1 px-1.5 text-xs"
                onClick={saveWebsite}
                disabled={savingWebsite}
              >
                <Save className="h-3 w-3" />
                {t("common.save")}
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="shrink-0 text-xs font-bold text-muted-foreground">https://</span>
          <Input
            data-testid="site-status-website"
            disabled={!editingWebsite || savingWebsite}
            className="font-mono text-xs font-bold"
            value={editingWebsite ? urlHostDraft : urlHostVal}
            onChange={(e) => setUrlHostDraft(e.target.value)}
          />
          <span className="shrink-0 text-xs font-bold text-muted-foreground">{`.${hostSuffix}`}</span>
        </div>
        {hostRenamePending && (
          <p className="mt-1.5 text-xs text-muted-foreground" data-testid="host-rename-progress">
            {t("dealership.hostRenameProgress", hostRename.step, HOST_RENAME_TOTAL_STEPS, hostRename.target_website_url)}
          </p>
        )}
        {hostRenameFailed && (
          <p className="mt-1.5 text-xs text-destructive" data-testid="host-rename-failed">
            {t("dealership.hostRenameFailed")}: {hostRename.error}
          </p>
        )}

        <div className="mt-4">
          <Label className="data-label mb-1 flex items-center gap-1">{t("dealership.helperUrl")}</Label>
          {helperUrl ? (
            <a
              data-testid="site-status-helper-url"
              href={helperUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm font-medium text-foreground hover:underline"
            >
              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
              {helperUrl}
            </a>
          ) : (
            <p className="text-sm text-muted-foreground" data-testid="site-status-helper-url-empty">
              {t("dealership.helperUrlEmpty")}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">{t("dealership.helperUrlHint")}</p>
        </div>
      </div>

      {canAssignExistingSite && (
        <div className="mt-6 border-t border-border pt-6">
          <Button
            data-testid="assign-existing-website-btn"
            variant="outline"
            size="sm"
            disabled={siteAssignPending || assigningSite}
            onClick={openAssignExistingSite}
          >
            {t("dealership.assignExistingWebsiteBtn")}
          </Button>
          {siteAssignPending && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="site-assign-progress">
              {t("dealership.assignExistingWebsiteProgress", siteAssign.step, SITE_ASSIGN_TOTAL_STEPS, siteAssign.target_site_global_id)}
            </p>
          )}
          {siteAssignFailed && (
            <p className="mt-2 text-xs text-destructive" data-testid="site-assign-failed">
              {t("dealership.assignExistingWebsiteFailed")}: {siteAssign.error}
            </p>
          )}
        </div>
      )}

      <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-border pt-6">
        <span className="data-label">{t("dealership.readiness.title")}</span>
        <Badge
          variant={readiness.consentsSetupCompleted ? "default" : "outline"}
          data-testid="readiness-consents-setup-completed"
        >
          {t("dealership.readiness.consentsSetupCompleted")}: {t(readiness.consentsSetupCompleted ? "dealership.readiness.yes" : "dealership.readiness.no")}
        </Badge>
        <Badge
          variant={readiness.dnsSetupCompleted ? "default" : "outline"}
          data-testid="readiness-dns-setup-completed"
        >
          {t("dealership.readiness.dnsSetupCompleted")}: {t(readiness.dnsSetupCompleted ? "dealership.readiness.yes" : "dealership.readiness.no")}
        </Badge>
      </div>

      <div className="mt-6 border-t border-border pt-6">
        <p className="mb-3 text-xs text-muted-foreground">{t("dealership.checkDataReadinessHint")}</p>
        <Button
          data-testid="check-data-readiness-btn"
          variant="outline"
          size="sm"
          disabled={checkingReadiness}
          onClick={checkReadiness}
        >
          {checkingReadiness ? t("common.loading") : t("dealership.checkDataReadiness")}
        </Button>
      </div>

      <AlertDialog open={!!readinessResult} onOpenChange={(open) => !open && setReadinessResult(null)}>
        <AlertDialogContent data-testid="data-readiness-result">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.dataReadinessResultTitle")}</AlertDialogTitle>
            <AlertDialogDescription data-testid="data-readiness-result-message">
              {readinessResult && readinessMessage(readinessResult, t)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="data-readiness-result-close" onClick={() => setReadinessResult(null)}>
              {t("common.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRenameOpen} onOpenChange={setConfirmRenameOpen}>
        <AlertDialogContent data-testid="host-rename-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.hostRenameConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dealership.hostRenameConfirmBody", `${(urlHostDraft || "").trim().toLowerCase()}.${hostSuffix}`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="host-rename-confirm-no">{t("common.no")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="host-rename-confirm-yes"
              disabled={savingWebsite}
              onClick={confirmRenameWebsite}
            >
              {t("common.yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={assignInputOpen} onOpenChange={setAssignInputOpen}>
        <AlertDialogContent data-testid="assign-existing-website-input">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.assignExistingWebsiteDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dealership.assignExistingWebsiteDialogBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">{t("dealership.assignExistingWebsiteInputLabel")}</label>
            <Input
              data-testid="assign-existing-website-input-field"
              value={siteGlobalIdDraft}
              onChange={(e) => setSiteGlobalIdDraft(e.target.value)}
              autoComplete="off"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assign-existing-website-input-cancel">
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="assign-existing-website-input-continue"
              disabled={!siteGlobalIdDraft.trim()}
              onClick={proceedToAssignConfirm}
            >
              {t("common.continue")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={assignConfirmOpen} onOpenChange={setAssignConfirmOpen}>
        <AlertDialogContent data-testid="assign-existing-website-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.assignExistingWebsiteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("dealership.assignExistingWebsiteConfirmBody", siteGlobalIdDraft.trim())}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="assign-existing-website-confirm-no">{t("common.no")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="assign-existing-website-confirm-yes"
              disabled={assigningSite}
              onClick={confirmAssignExistingSite}
            >
              {t("common.yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!siteAssignError} onOpenChange={(open) => !open && setSiteAssignError("")}>
        <AlertDialogContent data-testid="site-assign-error">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.assignExistingWebsiteSaveErrorTitle")}</AlertDialogTitle>
            <AlertDialogDescription data-testid="site-assign-error-message">
              {siteAssignError}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="site-assign-error-close" onClick={() => setSiteAssignError("")}>
              {t("common.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!websiteSaveError} onOpenChange={(open) => !open && setWebsiteSaveError("")}>
        <AlertDialogContent data-testid="website-save-error">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.websiteSaveErrorTitle")}</AlertDialogTitle>
            <AlertDialogDescription data-testid="website-save-error-message">
              {websiteSaveError}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction data-testid="website-save-error-close" onClick={() => setWebsiteSaveError("")}>
              {t("common.close")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="site-status-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.deleteSiteConfirmTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("dealership.deleteSiteConfirmBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">
              {t("dealership.deleteSiteConfirmInputLabel", websiteUrl)}
            </label>
            <Input
              data-testid="site-status-delete-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoComplete="off"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="site-status-delete-confirm-no">
              {t("dealership.deleteSiteConfirmNo")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="site-status-delete-confirm-yes"
              disabled={acting || !websiteUrl || confirmText !== websiteUrl}
              onClick={confirmDelete}
            >
              {t("dealership.deleteSiteConfirmYes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmAction} onOpenChange={(open) => !open && setConfirmAction(null)}>
        <AlertDialogContent data-testid="site-status-action-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("dealership.confirmActionTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction && t(`dealership.siteStatusConfirm.${confirmAction}`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="site-status-action-confirm-no">{t("common.no")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="site-status-action-confirm-yes"
              disabled={acting}
              onClick={confirmSimpleAction}
            >
              {t("common.yes")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
